import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Auth.module.css';
import Input from '../../components/Input';
import Button from '../../components/Button';
import PasswordStrength from '../../components/PasswordStrength';
import { useAuth } from '../../store/AuthContext';
import { useToast } from '../../store/ToastContext';
import { validators, validateForm } from '../../utils/validation';
import { MailIcon, ProfileIcon } from '../../components/Icons';

function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const toast = useToast();

  // Google Tag Manager - load once; only fire the gtm.js start event when the script is added
  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src*="googletagmanager.com/gtm.js?id=GTM-N2XCNLR4"]'
    );
    if (existingScript) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-N2XCNLR4';
    document.head.appendChild(script);
  }, []);
  // End Google Tag Manager

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    phone_number: '',
    password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    // Mobile number: digits only, max 10
    if (name === 'phone_number') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ignore stray spaces so " John " or "Me@Mail.com " don't fail or get stored as-is
    const payload = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      company_name: formData.company_name.trim(),
    };

    const validation = validateForm(payload, {
      name: [validators.required, validators.minLength(2)],
      email: [validators.required, validators.email],
      company_name: [validators.required, validators.minLength(2)],
      phone_number: [validators.required, validators.mobile],
      password: [validators.required, validators.password],
      confirm_password: [validators.required, validators.confirmPassword(formData.password)],
    });

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      await register(payload);
      toast.success('Registration successful! Please verify your email.');
      navigate('/verify-otp', { state: { email: payload.email, purpose: 'registration' } });
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>Create account</h2>
        <p className={styles.formSubtitle}>Get started with your DSC subscription</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <Input
          label="Full Name"
          name="name"
          required
          placeholder="Enter your name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          leftIcon={<ProfileIcon />}
          autoComplete="name"
        />

        <Input
          label="Email"
          name="email"
          type="email"
          required
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          leftIcon={<MailIcon />}
          autoComplete="email"
        />

        <div className={styles.formRow}>
          <Input
            label="Company Name"
            name="company_name"
            required
            placeholder="Company name"
            value={formData.company_name}
            onChange={handleChange}
            error={errors.company_name}
            autoComplete="organization"
          />

          <Input
            label="Mobile Number"
            name="phone_number"
            type="tel"
            inputMode="numeric"
            pattern="[6-9][0-9]{9}"
            maxLength={10}
            required
            placeholder="10-digit mobile number"
            value={formData.phone_number}
            onChange={handleChange}
            error={errors.phone_number}
            autoComplete="tel"
          />
        </div>

        <div>
          <Input
            label="Password"
            name="password"
            type="password"
            required
            placeholder="Create a password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            autoComplete="new-password"
          />
          <PasswordStrength password={formData.password} />
        </div>

        <Input
          label="Confirm Password"
          name="confirm_password"
          type="password"
          required
          placeholder="Confirm your password"
          value={formData.confirm_password}
          onChange={handleChange}
          error={errors.confirm_password}
          autoComplete="new-password"
        />

        <Button type="submit" fullWidth loading={loading}>
          Create Account
        </Button>
      </form>

      <p className={styles.formFooter}>
        Already have an account?{' '}
        <Link to="/login" className={styles.link}>
          Sign in
        </Link>
      </p>
    </div>
  );
}

export default Register;
